import { FC, useEffect, useState } from "react";

import { App, Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";

import {
  IContract,
  IPagination,
  IParams,
  ISimplifiedContractBilling,
} from "../../../../types";
import { Content } from "../../../../components/Content";
import { PageHeader } from "../../../../components/PageHeader";
import { reports } from "../../../../services/reports";
import { ActionTable } from "../../../../components/ActionTable";
import { ActionFunction } from "../../../../components/ActionColumn";
import { usePagination } from "../../../../hooks";
import { Filter } from "./Filter";
import { NotFound } from "../../../../components/NotFound";
import { api } from "../../../../services/api";

export async function getSimplifiedContractBilling(
  params: IParams,
  filter?: any
) {
  const { data } = await api.get<IPagination<ISimplifiedContractBilling>>(
    `/reports/simplified-billing-by-contract`,
    {
      params: {
        page: params.page,
        perPage: params.perPage,
        ...filter,
      },
    }
  );
  return { data: data.data, meta: data.meta };
}

const SimplifiedContractBilling: FC = () => {
  const { message } = App.useApp();
  const [initialLoad, setInitialLoad] = useState(false);
  const {
    data,
    isLoading,
    filter,
    setFilter,
    pagination,
    handleRefreshData,
    setData,
  } = usePagination(getSimplifiedContractBilling, { initialLoad: initialLoad });

  const expandedData =
    initialLoad && data?.length > 0
      ? data?.flatMap((item) =>
          item.contracts.map((contract) => ({
            ...item,
            contract: contract.contract,
            total: contract.total,
            id: contract.id,
          }))
        )
      : [];

  const columns: ColumnsType<
    ISimplifiedContractBilling & { contract: string }
  > = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (value) => {
        return (
          <div style={{ color: "#3030DA" }}>
            {value?.match(/\d/g).slice(0, 4).join("")}
          </div>
        );
      },
      width: 80,
    },
    {
      title: "Contrato",
      dataIndex: "contract",
      key: "contract",
      ellipsis: true,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      ellipsis: true,
    },
  ];

  const actions: ActionFunction<IContract> = (record) => {
    return [
      {
        key: "1",
        icon: <PrinterOutlined />,
        label: "Imprimir Registro",
        onClick: () => onPrint(record.id),
      },
    ];
  };

  useEffect(() => {
    const hasFilter =
      !!filter &&
      typeof filter === "object" &&
      !Array.isArray(filter) &&
      Object.keys(filter).length > 0;

    if (hasFilter) {
      setInitialLoad(true);
      return;
    }

    setInitialLoad(false);
    setData([]);
  }, [filter, handleRefreshData, setData]);

  const onPrint = (record?: string) => {
    if (record) {
      reports("simplified-billing-by-contract", { id: record });
      return;
    }
    if (filter && data.length > 0) {
      reports("simplified-billing-by-contract", filter);
      return;
    }
    if (filter && data.length === 0) {
      message.info("Não há registros para impressão.");
      return;
    }
    message.error("Você precisa filtar as informações!");
  };

  return (
    <>
      <PageHeader
        title="Faturamento simplificado por contrato"
        backButton={false}
        actions={[
          <Button
            onClick={() => onPrint()}
            key={"print_table"}
            icon={<PrinterOutlined />}
            type="primary"
            disabled={expandedData?.length === 0}
          >
            Imprimir
          </Button>,
        ]}
        crumb
      />
      <Content margin>
        <Filter onSubmit={(values) => setFilter(values)} filter={filter} />
      </Content>
      <Content margin>
        <ActionTable
          actions={actions}
          pagination={expandedData?.length > 0 ? pagination : false}
          size="small"
          loading={isLoading}
          rowKey={(record) => record.id}
          columns={columns}
          dataSource={expandedData}
          locale={{
            emptyText: <NotFound />,
          }}
        />
      </Content>
    </>
  );
};

export { SimplifiedContractBilling };
